import { create } from "zustand";

import {
    getCampaignDashboardState,
    saveCampaignDashboardState,
} from "../lib/campaignDashboardStorage";
import { CampaignFilterItem, CampaignFilterName } from "../types/campaign-filter";

import { SS_DATEFILTER_NAMES } from "@/entities/localstorage/model/const/localstorage_names";
import { GroupBy } from "@/shared/api-static/getHighlights";
import { calculateGroupBy } from "@/shared/lib/dateFilter/calculateGroupBy";
import {
    getSessionDateSelection,
    resolveSessionDateSelection,
    SessionDateSelection,
} from "@/shared/lib/dateFilter/sessionDateRange";
import { DATE } from "@/shared/model/const/services";

export interface CampaignFilterStore {
    filters: CampaignFilterItem[];
    start_date: string | null;
    end_date: string | null;
    group_date_by: GroupBy | null;
    date_selection: SessionDateSelection;
    chosenFilter: CampaignFilterName | null;
    setChosenFilter: (filter: CampaignFilterName | null) => void;

    setFilter: (filter: CampaignFilterItem) => void;
    applyDateSelection: (selection: SessionDateSelection) => void;
    restoreDashboardFilters: () => void;
    removeFilter: (filter: string) => void;
    flushFilters: () => void;
    resetDashboardFilters: () => void;
}

export const defaultCampaignDateSelection: SessionDateSelection = {
    mode: "preset",
    period: "last_7_days",
};

const resolveFilterDates = (
    selection: SessionDateSelection,
): Pick<CampaignFilterStore, "date_selection" | "start_date" | "end_date" | "group_date_by"> => {
    const [start, end] = resolveSessionDateSelection(selection);
    const startDate = start ? start.format(DATE.FORMAT) : null;
    const endDate = end ? end.format(DATE.FORMAT) : null;

    return {
        date_selection: selection,
        start_date: startDate,
        end_date: endDate,
        group_date_by: calculateGroupBy(start, end) || "day",
    };
};

const getStoredDashboardState = () => {
    const storedState = getCampaignDashboardState();
    if (storedState) return storedState;

    const legacyDateSelection = getSessionDateSelection(SS_DATEFILTER_NAMES.campaign);
    if (legacyDateSelection) {
        const migratedState = { filters: [], dateSelection: legacyDateSelection };
        saveCampaignDashboardState(migratedState);
        sessionStorage.removeItem(SS_DATEFILTER_NAMES.campaign);
        return migratedState;
    }

    return { filters: [], dateSelection: defaultCampaignDateSelection };
};

const initialDateState = resolveFilterDates(defaultCampaignDateSelection);

export const useCampaignFilterStore = create<CampaignFilterStore>((set, get) => ({
    filters: [],
    chosenFilter: null,
    ...initialDateState,

    setChosenFilter: (filter) => set({ chosenFilter: filter }),
    setFilter: (filter: CampaignFilterItem) => {
        set((state) => {
            if (state.filters.some((item) => item.name === filter.name))
                return {
                    filters: state.filters.map((item) =>
                        item.name === filter.name ? filter : item,
                    ),
                };
            return { filters: [...state.filters, filter] };
        });

        const state = get();
        saveCampaignDashboardState({
            filters: state.filters,
            dateSelection: state.date_selection,
        });
    },

    applyDateSelection: (selection) => {
        set(resolveFilterDates(selection));
        saveCampaignDashboardState({ filters: get().filters, dateSelection: selection });
    },
    restoreDashboardFilters: () => {
        const { filters, dateSelection } = getStoredDashboardState();
        set({ filters, chosenFilter: null, ...resolveFilterDates(dateSelection) });
    },

    removeFilter: (filter: string) => {
        set((state) => ({ filters: state.filters.filter((item) => item.name !== filter) }));

        const state = get();
        saveCampaignDashboardState({
            filters: state.filters,
            dateSelection: state.date_selection,
        });
    },

    flushFilters: () => {
        set({ filters: [] });
        saveCampaignDashboardState({
            filters: [],
            dateSelection: get().date_selection,
        });
    },

    resetDashboardFilters: () => {
        set({
            filters: [],
            chosenFilter: null,
            ...resolveFilterDates(defaultCampaignDateSelection),
        });
    },
}));
